import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Bell, ChevronDown, LogOut, Shield, CreditCard, User2, BarChart3 } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Navbar({ transparent = false }) {
  const { user, profile, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [scrolled, setScrolled] = React.useState(false);
  const [menu, setMenu] = React.useState(false);

  React.useEffect(() => {
    const f = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);

  const links = [
    { to: "/browse", label: "Home" },
    { to: "/browse?type=series", label: "Series" },
    { to: "/browse?type=movie", label: "Movies" },
    { to: "/live", label: "Live", live: true },
    { to: "/screens", label: "Multiplex" },
    { to: "/festival", label: "Festival" },
    { to: "/mylist", label: "My List" },
  ];

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent && !scrolled ? "bg-gradient-to-b from-black/80 to-transparent" : "glass-nav"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 flex items-center h-16">
        <Link to="/browse" data-testid="logo-link" className="flex items-center gap-2 mr-10">
          <span className="text-[#E50914] font-black text-2xl tracking-tight">CINECINEMA</span>
        </Link>
        <ul className="hidden md:flex items-center gap-6 text-sm">
          {links.map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-gray-300 hover:text-white transition-colors flex items-center gap-1.5 ${
                  loc.pathname + loc.search === l.to ? "text-white font-semibold" : ""
                }`}
              >
                {l.live && (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E50914] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E50914]"></span>
                  </span>
                )}
                {l.label}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/pricing" data-testid="nav-pricing" className="text-gray-300 hover:text-white">Pricing</Link>
          </li>
          {(user?.is_admin || user?.is_filmmaker) && (
            <li>
              <Link to="/dashboard" data-testid="nav-dashboard" className="text-[#f6c34a] hover:text-white flex items-center gap-1">
                <BarChart3 className="w-4 h-4" /> Dashboard
              </Link>
            </li>
          )}
          {user?.is_admin && (
            <li>
              <Link to="/admin" data-testid="nav-admin" className="text-[#E50914] hover:text-white flex items-center gap-1">
                <Shield className="w-4 h-4" /> Admin
              </Link>
            </li>
          )}
        </ul>
        <div className="flex-1" />
        <button data-testid="search-btn" onClick={() => nav("/search")} className="p-2 text-gray-300 hover:text-white">
          <Search className="w-5 h-5" />
        </button>
        <button className="p-2 text-gray-300 hover:text-white mr-2">
          <Bell className="w-5 h-5" />
        </button>
        <div className="relative">
          <button
            data-testid="user-menu-btn"
            onClick={() => setMenu((m) => !m)}
            className="flex items-center gap-2 text-sm"
          >
            <img
              src={profile?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.name || "user"}`}
              alt=""
              className="w-8 h-8 rounded-md bg-[#1a1a1a]"
            />
            <ChevronDown className={`w-4 h-4 text-gray-300 transition-transform ${menu ? "rotate-180" : ""}`} />
          </button>
          {menu && (
            <div data-testid="user-dropdown" className="absolute right-0 mt-3 w-56 bg-[#0f0f0f] border border-white/10 rounded-md py-2 shadow-2xl">
              <div className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider">Signed in as</div>
              <div className="px-4 py-1 text-sm text-white truncate">{user?.email}</div>
              <div className="my-2 border-t border-white/5" />
              <button
                onClick={() => { setMenu(false); nav("/profiles"); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-white/5 flex items-center gap-3"
              >
                <User2 className="w-4 h-4" /> Switch profile
              </button>
              <button
                onClick={() => { setMenu(false); nav("/account"); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-white/5 flex items-center gap-3"
              >
                <CreditCard className="w-4 h-4" /> Account & billing
              </button>
              <button
                data-testid="logout-btn"
                onClick={async () => { await logout(); nav("/"); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-white/5 flex items-center gap-3"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
